/**
 * m3 echo Frontend - Criterion Detail Card Component
 *
 * Displays detailed information about a single optimization criterion.
 * Supports both V1 (text/reasoning) and V2 (title/description/impact/priority/effort) formats.
 */

import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Chip,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Collapse,
  IconButton,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SignalCellularAltIcon from '@mui/icons-material/SignalCellularAlt';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { RecommendationItem, RecommendationItemV2, SubSignalResult } from '../types/api';
import { getScoreColor, getScoreLabel } from '../utils/scoringUtils';
import { safeText } from '../utils/safeText';
import WizardDraftDialog from './WizardDraftDialog';
import { billingApi } from '../services/billing';

interface CriterionData {
  score: number;
  label?: string;
  analysis: string;
  recommendations: (string | RecommendationItem | RecommendationItemV2)[];
  priority?: 'high' | 'medium' | 'low';
  expected_impact?: string;
  sub_signals?: SubSignalResult[];
}

/** Type guard for V2 recommendation */
function isV2Rec(item: unknown): item is RecommendationItemV2 {
  return typeof item === 'object' && item !== null && 'title' in item;
}

/** Normalizes a V1 recommendation entry */
function normalizeV1Recommendation(item: string | RecommendationItem): RecommendationItem {
  if (typeof item === 'string') {
    return { text: item, reasoning: '' };
  }
  return { text: item.text || '', reasoning: item.reasoning || '' };
}

interface CriterionDetailCardProps {
  name: string;
  icon: React.ReactNode;
  data: CriterionData;
  expanded?: boolean;
  onToggle?: () => void;
  scoringVersion?: number;
  projectId?: number;
  testId?: number;
  criterionKey?: string;
}

const CriterionDetailCard: React.FC<CriterionDetailCardProps> = ({
  name,
  icon,
  data,
  expanded = false,
  onToggle,
  scoringVersion = 1,
  projectId,
  testId,
  criterionKey,
}) => {
  const [wizardDialogOpen, setWizardDialogOpen] = useState(false);
  const [hasWizardModule, setHasWizardModule] = useState(false);
  const [selectedRecommendation, setSelectedRecommendation] = useState<{
    index: number;
    text: string;
    reasoning?: string;
  } | null>(null);
  const [subSignalsOpen, setSubSignalsOpen] = useState(false);

  useEffect(() => {
    billingApi.getMyConfig().then((res) => {
      setHasWizardModule(!!res.data.active_features['wizard']);
    }).catch(() => {});
  }, []);

  const handleWizardClick = (index: number, text: string, reasoning?: string) => {
    setSelectedRecommendation({ index, text, reasoning });
    setWizardDialogOpen(true);
  };

  const handleWizardClose = () => {
    setWizardDialogOpen(false);
    setSelectedRecommendation(null);
  };

  const getPriorityColor = (priority: string | number) => {
    if (typeof priority === 'number') {
      if (priority <= 1) return 'error';
      if (priority <= 2) return 'warning';
      if (priority <= 3) return 'info';
      return 'success';
    }
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getPriorityLabel = (priority: string | number): string => {
    if (typeof priority === 'number') {
      if (priority <= 1) return 'Kritisch';
      if (priority <= 2) return 'Hoch';
      if (priority <= 3) return 'Mittel';
      return 'Niedrig';
    }
    return priority === 'high' ? 'Hoch' : priority === 'medium' ? 'Mittel' : 'Niedrig';
  };

  const getPriorityIcon = (priority: string | number) => {
    if (typeof priority === 'number') {
      if (priority <= 1) return <ErrorIcon fontSize="small" />;
      if (priority <= 2) return <WarningIcon fontSize="small" />;
      return <CheckCircleIcon fontSize="small" />;
    }
    switch (priority) {
      case 'high': return <ErrorIcon fontSize="small" />;
      case 'medium': return <WarningIcon fontSize="small" />;
      case 'low': return <CheckCircleIcon fontSize="small" />;
      default: return null;
    }
  };

  if (!data || typeof data.score !== 'number') return null;

  const scoreColor = getScoreColor(data.score);
  const progressValue = Math.min(100, Math.max(0, data.score));
  const isV2 = scoringVersion >= 2;

  return (
    <Accordion
      expanded={expanded}
      onChange={onToggle}
      sx={{ mb: 1 }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box display="flex" alignItems="center" width="100%" gap={2}>
          <Box sx={{ color: scoreColor }}>{icon}</Box>
          <Box flex={1}>
            <Typography variant="subtitle1" fontWeight="medium">
              {name}
            </Typography>
            {isV2 && data.label && (
              <Typography variant="caption" sx={{ color: scoreColor }}>
                {safeText(data.label)}
              </Typography>
            )}
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="h6" sx={{ color: scoreColor, minWidth: 40 }}>
              {data.score}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails>
        {/* Progress Bar */}
        <Box mb={3}>
          <LinearProgress
            variant="determinate"
            value={progressValue}
            sx={{
              height: 10,
              borderRadius: 5,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: scoreColor,
              },
            }}
          />
          <Box display="flex" justifyContent="space-between" mt={0.5}>
            <Typography variant="caption" color="text.secondary">0</Typography>
            <Typography variant="caption" sx={{ color: scoreColor, fontWeight: 500 }}>
              {isV2 ? getScoreLabel(data.score) : data.score}
            </Typography>
            <Typography variant="caption" color="text.secondary">100</Typography>
          </Box>
        </Box>

        {/* Priority Indicator (V1) */}
        {!isV2 && data.priority && (
          <Box mb={2}>
            <Chip
              {...(getPriorityIcon(data.priority) && { icon: getPriorityIcon(data.priority)! })}
              label={`Priorität: ${getPriorityLabel(data.priority)}`}
              color={getPriorityColor(data.priority) as any}
              size="small"
              variant="outlined"
            />
          </Box>
        )}

        {/* Analysis */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom fontWeight="medium">
            Analyse
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {safeText(data.analysis)}
          </Typography>
        </Box>

        {/* Sub-Signals (V2 only) */}
        {isV2 && data.sub_signals && data.sub_signals.length > 0 && (
          <Box mb={2}>
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => setSubSignalsOpen(!subSignalsOpen)}
            >
              <SignalCellularAltIcon fontSize="small" color="action" />
              <Typography variant="subtitle2" fontWeight="medium">
                Sub-Signale ({data.sub_signals.length})
              </Typography>
              <IconButton size="small">
                {subSignalsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            </Box>
            <Collapse in={subSignalsOpen}>
              <Box mt={1} pl={1}>
                {data.sub_signals.map((signal, idx) => (
                  <Box key={idx} mb={1.5}>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
                      <Typography variant="body2">{safeText(signal.name)}</Typography>
                      <Typography variant="body2" sx={{ color: getScoreColor(signal.score), fontWeight: 500 }}>
                        {signal.score}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, Math.max(0, signal.score))}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: getScoreColor(signal.score),
                        },
                      }}
                    />
                    {signal.findings && signal.findings.length > 0 && (
                      <Box mt={0.5}>
                        {signal.findings.map((f, fi) => (
                          <Typography key={fi} variant="caption" color="text.secondary" display="block">
                            &bull; {safeText(f)}
                          </Typography>
                        ))}
                      </Box>
                    )}
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        )}

        {/* Recommendations */}
        <Box mb={2}>
          <Typography variant="subtitle2" gutterBottom fontWeight="medium" display="flex" alignItems="center" gap={1}>
            <TipsAndUpdatesIcon fontSize="small" />
            Empfehlungen
          </Typography>
          <List dense>
            {data.recommendations.map((raw, index) => {
              if (isV2 && isV2Rec(raw)) {
                // V2 format: title, description, impact, priority, effort
                const rec = raw as RecommendationItemV2;
                return (
                  <ListItem key={index} sx={{ pl: 0, alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 36, mt: 0.5 }}>
                      <CheckCircleIcon fontSize="small" color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box>
                          <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
                            <Typography variant="body2" fontWeight="medium">{safeText(rec.title)}</Typography>
                            <Chip
                              label={getPriorityLabel(rec.priority)}
                              size="small"
                              color={getPriorityColor(rec.priority) as any}
                              variant="outlined"
                              sx={{ height: 20, fontSize: '0.7rem' }}
                            />
                            {/* Layer 6 Wizard Button */}
                            {hasWizardModule && projectId && testId && criterionKey && (
                              <Tooltip title="Entwurf generieren" arrow placement="top">
                                <Box
                                  component="button"
                                  onClick={(e: React.MouseEvent) => {
                                    e.stopPropagation();
                                    handleWizardClick(index, rec.title, rec.description);
                                  }}
                                  sx={{
                                    ml: 0.5,
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.3,
                                    padding: '2px 6px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    fontSize: '11px',
                                    fontWeight: 500,
                                    cursor: 'pointer',
                                    '&:hover': {
                                      background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                                    },
                                  }}
                                >
                                  <AutoAwesomeIcon sx={{ fontSize: 12 }} />
                                  Wizard
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                          {rec.description && (
                            <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                              {safeText(rec.description)}
                            </Typography>
                          )}
                          {rec.impact && (
                            <Typography variant="caption" color="primary" display="block">
                              Impact: {safeText(rec.impact)}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                );
              }

              // V1 format: text + optional reasoning
              const rec = normalizeV1Recommendation(raw as string | RecommendationItem);
              return (
                <ListItem key={index} sx={{ pl: 0 }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <CheckCircleIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={0.5}>
                        <span>{safeText(rec.text)}</span>
                        {rec.reasoning && (
                          <Tooltip
                            title={safeText(rec.reasoning)}
                            arrow
                            placement="top"
                            enterTouchDelay={50}
                            slotProps={{
                              tooltip: { sx: { maxWidth: 350 } },
                            }}
                          >
                            <HelpOutlineIcon
                              sx={{ fontSize: 18, color: 'text.disabled', cursor: 'help', flexShrink: 0 }}
                            />
                          </Tooltip>
                        )}
                        {/* Layer 6 Wizard Button */}
                        {hasWizardModule && projectId && testId && criterionKey && (
                          <Tooltip title="Entwurf generieren" arrow placement="top">
                            <Box
                              component="button"
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                handleWizardClick(index, rec.text, rec.reasoning);
                              }}
                              sx={{
                                ml: 0.5,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.3,
                                padding: '2px 6px',
                                border: 'none',
                                borderRadius: '4px',
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: 'white',
                                fontSize: '11px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                '&:hover': {
                                  background: 'linear-gradient(135deg, #5a6fd6 0%, #6a4190 100%)',
                                },
                              }}
                            >
                              <AutoAwesomeIcon sx={{ fontSize: 12 }} />
                              Wizard
                            </Box>
                          </Tooltip>
                        )}
                      </Box>
                    }
                    primaryTypographyProps={{
                      variant: 'body2',
                      component: 'div',
                    }}
                  />
                </ListItem>
              );
            })}
          </List>
        </Box>

        {/* Expected Impact (V1 only) */}
        {!isV2 && data.expected_impact && (
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="medium">
              Erwarteter Einfluss auf LLM-Ranking
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {safeText(data.expected_impact)}
            </Typography>
          </Box>
        )}
      </AccordionDetails>

      {/* Layer 6 Wizard Draft Dialog */}
      {projectId && testId && criterionKey && selectedRecommendation && (
        <WizardDraftDialog
          open={wizardDialogOpen}
          onClose={handleWizardClose}
          projectId={projectId}
          testId={testId}
          criterionKey={criterionKey}
          criterionName={name}
          recommendationIndex={selectedRecommendation.index}
          recommendationText={selectedRecommendation.text}
          recommendationReasoning={selectedRecommendation.reasoning}
        />
      )}
    </Accordion>
  );
};

export default CriterionDetailCard;
